// This file contains all TypeScript interfaces used throughout the application

import type {DateTime} from "luxon";

export interface AppConfig extends LogConfig, ApiConfig{
    debug: boolean;
    iva: number;
}

// From api/index.ts
export interface ApiConfig {
    apiServer: string;
}


export enum LogLevel { Error, Warning, Info, Debug}

// From log/index.ts
export interface LogConfig {
    logEnabled: boolean;
    logLevel: LogLevel;
}


export interface DrawerItem {
    title: string,
    icon: string,
    link?: string,
    items?: DrawerItem[]
}

// DB models interfaces

export interface Address {
    name: string;
    city: string;
    province: string;
    zip_code: string;
}


export enum UserRole {
    root = "root",
    admin = "admin",
    editor = "editor",
    viewer = "viewer",
    user = "user",
    guest = "guest"
}

export interface User extends DbDoc {
    _id?: string;
    email: string;
    password: string;
    temp_password: string;
    name: string;
    surname: string;
    height: number;
    weight: number;
    training: number;
    fiscal_code: string;
    address: Address;
    phone: string;
    user_type: string;
    role: UserRole;
    date_of_birth?: string;
    city_of_birth: string;
    province_of_birth: string;
    register_date?: string;
    active: boolean;
    sandbox: boolean;
    ongoing_lessons: string[];
    done_lessons: string[];
    ongoing_products: string[];
    done_products: string[];
    certificate: boolean;
    privacy_1: boolean;
    privacy_2: boolean;
    privacy_3: boolean;
    home_kit: boolean;
    identity_card_front: string;
    identity_card_rear: string;
    medical_cert: string;
    medical_cert_expiration?: string;
    scheda: string;
    green_pass_expiration?: string;
    notes: string;
    user_notes: string;
    pause_subscription: boolean;
    num_pauses: number;
}



// Common interfaces
export interface GenericReply {
    success: boolean;
    status: number;
    message?: string;
}

export interface DbDoc {
    _id?: string;
}

export interface AuthReply extends GenericReply {
    token?: string;
    user?: User;
}

export interface DocReply<T> extends GenericReply {
    data?: T;
}
export interface DashboardReply extends GenericReply {
    data?: Dashboard;
}

export interface SimpleListReply<T> extends GenericReply {
    data?: T[];
}


export interface ListReply<T> extends SimpleListReply<T> {
    count?: number;
    page?: number;
    pages?: number;
}



export interface Sort {
    [index: string]: number;
}

// From consts.ts
export interface OptionItem {
    title: string;
    abbrev?: string;
    value: string;
    color?: string;
}

export enum SeatsType {
    exp = "topgymexperience",
    corsi = "corsi",
    opengym = "opengym",
    vacu = "vacu",
    gym = "gym"
}

export const seatsTypeOptions = [
    { title : 'TopGym Experience', value : SeatsType.exp},
    { title : 'Sala Corsi', value : SeatsType.corsi},
    { title : 'Open Gym', value : SeatsType.opengym},
    { title : 'Vacu', value : SeatsType.vacu},
    /*{ title : 'One-to-One', value : S},*/
    { title : 'Bike indoor', value : SeatsType.gym},
];


// Generated interfaces from Golang structs
export interface CalendarHour extends DbDoc {
    _id?: string;
    name: string;
    type: SeatsType;
    day: number;
    time: string;
    duration: number;
    max_seats: number;
    restricted: boolean;
    exclude_users: string[];
}

export interface Holiday extends DbDoc {
    _id?: string;
    from: string;
    to: string;
}

export interface Banner extends DbDoc {
    _id?: string;
    title: string;
    media_id: string;
    link: string;
    exp?: string;
    priority: number;
    duration: number;
    pub: boolean;
}

export interface BannerDisplay extends DbDoc {
    _id?: string;
    title: string;
    media_id: string;
    src: string;
    link: string;
    exp?: string;
    priority: number;
    duration: number;
    pub: boolean;
}

export interface BookingPref extends DbDoc {
    _id?: string;
    name: string;
    seats_type: SeatsType;
    limit: number;
}

export interface Booking extends DbDoc {
    _id?: string;
    user_id: string;
    user_seat_id: string;
    calendar_hour_id: string;
    type: SeatsType;
    date: string;
    prefs_ids: string[];
}

export interface BookingDisplay extends DbDoc {
    _id?: string;
    user_id: string;
    user_seat_id: string;
    calendar_hour_id: string;
    user: User;
    user_seat?: UserSeat;
    calendar_hour: CalendarHour;
    type: SeatsType;
    date: string;
    prefs_ids: string[];
    prefs: BookingPref[];
}

export interface Cart extends DbDoc {
    _id?: string;
    user_id: string;
    items: string[];
    sub_pause_days: number;
    expedition: boolean;
    payment_method: string;
    payment_uid: string;
    paypal_detail: string;
    paypal_status: string;
    status: string;
    creation_date?: string;
    last_update?: string;
    address?: Address;
    home_kit: boolean;
    card: boolean;
    block_edit: boolean;
    evaded: boolean;
    paypal_request_id: string;
}

export interface CartDisplay extends DbDoc {
    _id?: string;
    user_id: string;
    user: User;
    items: Product[];
    sub_pause_days: number;
    expedition: boolean;
    payment_method: string;
    payment_uid: string;
    status: string;
    address?: Address;
    creation_date: string;
    last_update?: string;
    block_edit: boolean;
    home_kit: boolean;
    card: boolean;
    evaded: boolean;
    paypal_request_id: string;
}

export interface CmsPage extends DbDoc {
    _id?: string;
    path: string;
    title: Record<string, string>;
    content: Record<string, string>;
    video_id?: string;
    pub: boolean;
    last_update?: string;
}

export interface ConiCard extends DbDoc {
    _id?: string;
    user_id?: string;
    number: string;
    name: string;
    surname: string;
    fiscal_code: string;
    expiration: string;
    insurance: string;
    not_imported: boolean;
    //display
    user?:User
}

export interface GalleryImage extends DbDoc {
    _id?: string;
    title: string;
    icon_id: string;
    media_id: string;
    priority: number;
    pub: boolean;
    icon?:string
    src?:string
}

export interface Dashboard {
    num_users: number;
    num_kits: number;
    num_exercises: number;
    num_categories: number;
    num_genre: number;
    num_difficulties: number;
    num_media: number;
    num_cards: number;
    num_reports: number;
    home_sections: number;
    num_subcategories: number;
    today_bookings_exp: number;
    tomorrow_bookings_exp: number;
    today_bookings_corsi: number;
    tomorrow_bookings_corsi: number;
    today_bookings_vacu: number;
    tomorrow_bookings_vacu: number;
    today_bookings_opengym: number;
    today_bookings_gym: number;
    tomorrow_bookings_opengym: number;
    tomorrow_bookings_gym: number;
    num_sells_seats_7_days: number[];
    num_sells_fit_7_days: number[];
    num_sells_7_days: number[];
    sells_seats_7_days: number[];
    sells_fit_7_days: number[];
    sells_7_days: number[];
    num_sells_seats: number;
    num_sells_fit: number;
    num_sells: number;
    sells_seats: number;
    sells_fit: number;
    sells: number;
    this_month_exp_subscription: number;
    this_month_vacu_subscription: number;
    this_month_corsi_subscription: number;
    this_month_open_gym_subscription: number;
    this_month_gym_subscription: number;
    num_unused_subscription: number;
    exp_seats: UserSeat[];
    vacu_seats: UserSeat[];
    corsi_seats: UserSeat[];
    open_gym_seats: UserSeat[];
    gym_seats: UserSeat[];
    unused_seats: UserSeat[];
}

export interface HomeSection extends DbDoc {
    _id?: string;
    name: Record<string, string>;
    type: string;
    type_filter?: string;
    order: number;
    max_items: number;
    items: string[];
    last_update?: string;
}

export interface Kit extends DbDoc {
    _id?: string;
    title: Record<string, string>;
    description: Record<string, string>;
    category: string;
    genre: string;
    difficulty: string;
    cover: string;
    icon: string;
    last_update?: string;
    order: number;
    pub: boolean;
}

export interface KitDisplay extends DbDoc {
    _id?: string;
    title: Record<string, string>;
    description: Record<string, string>;
    category: Category;
    genre: Genre;
    difficulty: Difficulty;
    cover: Media;
    icon: Media;
    last_update?: string;
    order: number;
    pub: boolean;
}

export interface Schedule {
    work_time: number;
    recovery_time: number;
    exercise_id: string;
}

export interface Exercise extends DbDoc {
    _id?: string;
    title: Record<string, string>;
    description: Record<string, string>;
    video_id: string;
    audio_id: string;
    video_loop_start: number;
    video_loop_end: number;
    last_update?: string;
}

export interface Lesson extends DbDoc {
    _id?: string;
    title: Record<string, string>;
    description: Record<string, string>;
    product_id: string;
    video_id: string;
    schedules: Schedule[];
    last_update?: string;
    android_video_mode: boolean;
}

export interface LessonDisplay extends DbDoc {
    _id?: string;
    title: Record<string, string>;
    description: Record<string, string>;
    product_id: string;
    video_id: string;
    video?: Media;
    schedules: Schedule[];
    exercises: Exercise[];
    exercises_video: Media[];
    exercises_audio: Media[];
    last_update?: string;
    android_video_mode: boolean;
}

export type MediaType = 'audio' | 'video' | 'icon' | 'cover' | 'banner' | 'gallery';

export interface Media extends DbDoc {
    _id?: string;
    name: string;
    media: MediaType;
    url: string;
    last_update?: string;
}

export interface MedicalHour extends DbDoc {
    _id?: string;
    from: string;
    to: string;
}

export interface MedicalCalendarBooking extends DbDoc {
    _id?: string;
    when: string;
    user_id: string;
}

export interface Category extends DbDoc {
    _id?: string;
    name: Record<string, string>;
    abbrev: Record<string, string>;
    desc: Record<string, string>;
    is_gym_category: boolean;
    last_update?: string;
}

export interface Subcategory extends DbDoc {
    _id?: string;
    name: Record<string, string>;
    cover: string;
    last_update?: string;
}

export interface Genre extends DbDoc {
    _id?: string;
    name: Record<string, string>;
    abbrev: Record<string, string>;
    desc: Record<string, string>;
    last_update?: string;
}

export interface Difficulty extends DbDoc {
    _id?: string;
    name: Record<string, string>;
    abbrev: Record<string, string>;
    desc: Record<string, string>;
    value: number;
    last_update?: string;
}

export interface Mail extends DbDoc {
    _id?: string;
    mail: string;
}

export interface Notification extends DbDoc {
    _id?: string;
    time: string;
    text: string;
    link: string;
    to: string[];
    read_by: string[];
}

export type ProductType = 'mixed' | 'distinct' | 'video';

export interface ProductSeats {
    seats_types: SeatsType[];
    num_seats: number;
    consumable: boolean;
    duration: number;
    main: boolean;
}

export interface Product extends DbDoc {
    _id?: string;
    kit_id?: string;
    subcategory_id?: string;
    title: Record<string, string>;
    description: Record<string, string>;
    days_interval: number;
    sku: string;
    type: ProductType;
    is_seats_product: boolean;
    seats_duration: number;
    seats_types: SeatsType[];
    num_seats: number;
    consumable: boolean;
    price: number;
    second_price: number;
    expedition: boolean;
    seats_has_same_expiration: boolean;
    last_update?: string;
    seats?: ProductSeats[];

    kit?:Kit
}

export interface Purchase extends DbDoc {
    _id?: string;
    user_id: string;
    product_id: string;
    product_sku: string;
    type: ProductType;
    seats: ProductSeats[];
    seats_types: SeatsType[];
    num_seats: number;
    lessons_shuffle_seed: number;
    ongoing_lessons: string[];
    done_lessons: string[];
    consumed: boolean;
    date: string;
    expiration?: string;
}

export interface Report extends DbDoc {
    _id?: string;
    user_id: string;
    kit_id: string;
    product_id: string;
    lesson_id: string;
    date: string;
    weight: number;
    trainings_per_week: number;
    borg: number;
    perceived_fatigue: boolean;
    perceived_fatigue_notes: string;
    perceived_fatigue_progress: boolean;
    perceived_fatigue_progress_notes: string;
    perceived_fatigue_all: boolean;
    perceived_fatigue_all_notes: string;
    training_change_question: boolean;
    training_change_question_notes: string;
    trainings_not_understood: boolean;
    trainings_not_understood_list: string[];
    trainings_failed: boolean;
    trainings_failed_list: string[];
    training_in_time: boolean;
    training_in_time_notes: string;
    feelings: number;
    notes: string;
    //display
    user?: User;
    kit?:Kit
    product?:Product,
    lesson?:Lesson
}
export interface ReportDisplay extends DbDoc {
    _id?: string;
    user_id: string;
    kit_id: string;
    product_id: string;
    lesson_id: string;
    date: string;
    weight: number;
    trainings_per_week: number;
    borg: number;
    perceived_fatigue: boolean;
    perceived_fatigue_notes: string;
    perceived_fatigue_progress: boolean;
    perceived_fatigue_progress_notes: string;
    perceived_fatigue_all: boolean;
    perceived_fatigue_all_notes: string;
    training_change_question: boolean;
    training_change_question_notes: string;
    trainings_not_understood: boolean;
    trainings_not_understood_list: Exercise[];
    trainings_failed: boolean;
    trainings_failed_list: Exercise[];
    training_in_time: boolean;
    training_in_time_notes: string;
    feelings: number;
    notes: string;
    //display
    user?: User;
    kit?:Kit
    product?:Product,
    lesson?:Lesson
}

export interface ExtraSeatsPause extends DbDoc {
    _id?: string;
    user_id?: string;
    from: string;
    to: string;
    num_days: number;
}

export interface HolidaysSettings {
    natale: boolean;
    santo_stefano: boolean;
    capodanno: boolean;
    epifania: boolean;
    pasquetta: boolean;
    liberazione: boolean;
    lavoro: boolean;
    repubblica: boolean;
    ferragosto: boolean;
    santi: boolean;
    immacolata: boolean;
}

export interface Week {
    lun:boolean
    mar:boolean
    mer:boolean
    gio:boolean
    ven:boolean
    sab:boolean
    dom:boolean
}

export interface Settings extends DbDoc {
    _id?: string;
    coni_card_price: number;
    home_kit_price: number;
    home_kit_expedition_price: number;
    home_kit_expedition_days: number;
    purchase_expiration_days_after_first_use: number;
    last_update?: string;
    open_days: Week;
    user_bookings_per_week: number;
    user_bookings_per_day: number;
    bookings_per_hour_exp: number;
    bookings_per_hour_corsi: number;
    bookings_per_hour_macchine: number;
    bookings_per_hour_vacu: number;
    bookings_per_hour_gym: number;
    cancel_within_minutes: number;
    booking_within_minutes: number;
    booking_within_minutes_exp: number;
    booking_within_minutes_corsi: number;
    booking_within_minutes_macchine: number;
    booking_within_minutes_vacu: number;
    booking_within_minutes_gym: number;
    holidays: HolidaysSettings;
    day_of_sport_year_start: number;
    month_of_sport_year_start: number;
    max_days_of_subscription_pause: number;
    extra_day_of_sub_pause_price: number;
    banner_duration: number;
}

export interface Stat extends DbDoc {
    _id?: string;
    user_id?: string;
    date: string;
}

export interface UserSeat extends DbDoc {
    _id?: string;
    user_id: string;
    consumable: boolean;
    seats_types: SeatsType[];
    available: number;
    used: number;
    duration: number;
    product_id?: string;
    purchase_id?: string;
    main_user_seats_id?: string;
    shared_expiration: boolean;
    expiration?: string;
    // not in db - for dashboard
    user_name?:string
}




// From stores/index.ts
export interface StoreUser {
    _id: string;
    role: string;
    [key: string]: any;
}

export interface State {
    token: string | null;
    loading: boolean;
    loadingMessage: string;
    loginDialog: boolean;
    profile: StoreUser | null;
    dialogMessage: string;
    successDialog: boolean;
    warningDialog: boolean;
    errorDialog: boolean;
    infoDialog: boolean;
    confirmDialog: boolean;
    confirmDialogTitle: string;
    confirmDialogMessage: string;
    confirmDialogCallback: ((accepted: boolean) => void) | null;
    media: any[];
    difficulties: any[];
    categories: any[];
    genre: any[];
    exercises: any[];
    users: any[];
    kits: any[];
    products: any[];
    purchases: any[];
    notifications: any[];
    packets: any[];
    bookingprefs: any[];
    subcategories: any[];
    userseats: any[];
    cards: any[];
    pauses: any[];
    settings: Record<string, any>;
}

export interface SortItem {
    key: string
    order?: boolean | 'asc' | 'desc';
}


export interface Pagination {
    page : number
    sort : Array<SortItem>
    limit : number
    count: number
}

export interface Query {
    cf?:string
    name?:string
    fullname?:string
    email?:string
    number?:string
    role?:string
    valid?:boolean
    expired?:boolean
    user?:string
    active?:boolean
    certexp?:string
    from?:string
    to?:string
    title?:string
    genre?:string
    ctg?:string
    diff?:string
    media?:string
    product?:string
    nodraft?:string|boolean
    sku?:string
    date_from?:string
    date_to?:string

}

export interface DataTableError {
    status: number
    message: string
}




export interface BookingEvent {
    _id : string
    item : BookingDisplay,
    title : string
    titleSmall : string
    certStatus : 'none'|'valid'|'expiring'|'expired',
    certExpire : string,
    noCard : boolean,
    cardExpire : string,
    genre : 'M'|'F',
    when : DateTime,
    noUserSeatError : boolean
}
export interface MedicalEvent {
    _id : string
    item : MedicalCalendarBooking,
    title : string
    when : DateTime,
}

export interface MedicalTime {
    time : string
    date : DateTime
}